import { useTranslate } from "@refinedev/core";
import { BarChart3, Brush, CalendarDays, ClipboardCheck, Home, Pill, Scale, Sun, Utensils } from "lucide-react";

import type { Onderdeel } from "./pensionoverzichten/velden";
import "./PensionOverzichten.css";

/**
 * De onderdelenbalk van Pensionoverzichten: Pension, Dagopvang, Reserveringen en Bezetting, en daarachter de
 * dagelijkse lijsten Wegen, Bodycheck, Borstelen, Medicatie en Voerlijst (Maarten, 2026-10-05: "deze moeten bij
 * pensionoverzichten komen zoals we dat ook hierboven hebben gedaan"). Die vijf waren eigen menu-items.
 *
 * <p>De eerste vier zijn onderdelen binnen het scherm Pensionoverzichten; de lijsten blijven hun eigen pagina met hun
 * eigen url, zodat een bladwijzer op /pension-wegen gewoon blijft werken. Boven die pagina's staat dezelfde balk.
 */
const ONDERDELEN: Onderdeel[] = ["PENSION", "DAGOPVANG", "RESERVERINGEN", "BEZETTING"];
const ONDERDEEL_ICOON = { PENSION: Home, DAGOPVANG: Sun, RESERVERINGEN: CalendarDays, BEZETTING: BarChart3 } as const;

export const PENSION_LIJSTEN = [
  { page: "pension-wegen", labelKey: "menu.pensionWegen", icon: Scale },
  { page: "pension-bodycheck", labelKey: "menu.pensionBodycheck", icon: ClipboardCheck },
  { page: "pension-borstelen", labelKey: "menu.pensionBorstelen", icon: Brush },
  { page: "pension-medicatie", labelKey: "menu.pensionMedicatie", icon: Pill },
  { page: "pension-voerlijst", labelKey: "menu.pensionVoerlijst", icon: Utensils },
] as const;

export type PensionLijstPage = (typeof PENSION_LIJSTEN)[number]["page"];

/** Pensionoverzichten zelf of een van de lijsten erachter: dan hoort Pensionoverzichten in het menu aan te staan. */
export function isPensionModulePage(page: string): boolean {
  return page === "pension-overzichten" || PENSION_LIJSTEN.some((m) => m.page === page);
}

export function isPensionLijstPage(page: string): page is PensionLijstPage {
  return PENSION_LIJSTEN.some((m) => m.page === page);
}

export function PensionOnderdelenBalk({
  actief,
  onOnderdeel,
  onLijst,
}: {
  actief: Onderdeel | PensionLijstPage;
  onOnderdeel: (o: Onderdeel) => void;
  onLijst: (page: PensionLijstPage) => void;
}) {
  const t = useTranslate();
  return (
    <div className="po-onderdelen" role="tablist" aria-label={t("pensionOverzichten.onderdelen")}>
      {ONDERDELEN.map((o) => {
        const Icoon = ONDERDEEL_ICOON[o];
        return (
          <button key={o} type="button" role="tab" aria-selected={actief === o} data-actief={actief === o || undefined}
            onClick={() => onOnderdeel(o)}>
            <Icoon size={16} aria-hidden="true" />
            {t(`pensionOverzichten.onderdeel.${o}`)}
          </button>
        );
      })}
      <span className="po-onderdelen__scheiding" aria-hidden="true" />
      {PENSION_LIJSTEN.map(({ page, labelKey, icon: Icoon }) => (
        <button key={page} type="button" role="tab" aria-selected={actief === page} data-actief={actief === page || undefined}
          onClick={() => onLijst(page)}>
          <Icoon size={16} aria-hidden="true" />
          {t(labelKey)}
        </button>
      ))}
    </div>
  );
}

/** De balk boven een van de lijsten (Wegen, Bodycheck, …), los van het scherm Pensionoverzichten. */
export function PensionLijstBalk(props: Parameters<typeof PensionOnderdelenBalk>[0]) {
  return (
    <div className="po po--alleen-balk">
      <PensionOnderdelenBalk {...props} />
    </div>
  );
}
